import { useRef, useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Languages, Loader2, Copy, Check, X, RotateCcw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { translateText } from '@/services/translation-service'

type Phase = 'hidden' | 'icon' | 'loading' | 'result' | 'error'

export function SelectionTranslationTooltip() {
  const { t, i18n } = useTranslation()
  const iconRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [phase, setPhase] = useState<Phase>('hidden')
  const [iconPos, setIconPos] = useState({ x: 0, y: 0 })
  const [capturedText, setCapturedText] = useState('')
  const [translatedText, setTranslatedText] = useState('')
  const [copied, setCopied] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  const [modalPos, setModalPos] = useState({ x: 0, y: 0 })
  const dragging = useRef(false)

  const phaseRef = useRef<Phase>('hidden')
  phaseRef.current = phase

  // Target language = user's current language (translate foreign text into user's language)
  const targetLanguage = i18n.language?.startsWith('de') ? 'de' : 'en'

  const centerModal = useCallback(() => {
    setModalPos({
      x: Math.round(window.innerWidth / 2 - 160),
      y: Math.round(window.innerHeight / 2 - 80),
    })
  }, [])

  // Show small translate icon at end of selection
  useEffect(() => {
    const onMouseUp = (e: MouseEvent) => {
      if (iconRef.current?.contains(e.target as Node)) return
      if (panelRef.current?.contains(e.target as Node)) return
      if (dragging.current) return
      const p = phaseRef.current
      if (p !== 'hidden' && p !== 'icon') return

      requestAnimationFrame(() => {
        const sel = window.getSelection()
        if (!sel || sel.rangeCount === 0) return
        const selectedText = sel.toString().trim()
        if (selectedText.length < 3) return

        const range = sel.getRangeAt(0)
        const rect = range.getBoundingClientRect()
        let x = rect.right + 4
        let y = rect.bottom - 4
        x = Math.min(x, window.innerWidth - 36)
        y = Math.min(y, window.innerHeight - 36)

        setCapturedText(selectedText.slice(0, 2000))
        setIconPos({ x, y })
        setPhase('icon')
      })
    }

    const onMouseDown = (e: MouseEvent) => {
      if (iconRef.current?.contains(e.target as Node)) return
      if (panelRef.current?.contains(e.target as Node)) return
      if (phaseRef.current === 'hidden') return
      abortRef.current?.abort()
      setPhase('hidden')
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phaseRef.current !== 'hidden') {
        abortRef.current?.abort()
        setPhase('hidden')
      }
    }

    document.addEventListener('mouseup', onMouseUp)
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mouseup', onMouseUp)
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [centerModal])

  // Click icon → open modal and start translation immediately
  const handleIconClick = useCallback(() => {
    if (!capturedText) return

    centerModal()

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    setPhase('loading')

    translateText(capturedText, targetLanguage, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) {
          setTranslatedText(result.translated_text)
          setPhase('result')
        }
      })
      .catch((err: any) => {
        if (err?.name === 'AbortError') return
        setPhase('error')
      })
  }, [capturedText, targetLanguage, centerModal])

  const handleRetry = useCallback(() => {
    if (!capturedText) return
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    setPhase('loading')

    translateText(capturedText, targetLanguage, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) {
          setTranslatedText(result.translated_text)
          setPhase('result')
        }
      })
      .catch((err: any) => {
        if (err?.name === 'AbortError') return
        setPhase('error')
      })
  }, [capturedText, targetLanguage])

  // Drag handling — identical to AppWindow
  const dragData = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null)
  const modalPosRef = useRef({ x: 0, y: 0 })
  modalPosRef.current = modalPos

  const onMouseMoveDrag = useCallback((e: MouseEvent) => {
    const d = dragData.current
    if (!d) return
    const nx = d.origX + (e.clientX - d.startX)
    const ny = d.origY + (e.clientY - d.startY)
    modalPosRef.current = { x: nx, y: ny }
    const el = panelRef.current
    if (el) {
      el.style.left = `${nx}px`
      el.style.top = `${ny}px`
    }
  }, [])

  const onMouseUpDrag = useCallback(() => {
    dragData.current = null
    dragging.current = false
    document.body.style.userSelect = ''
    setModalPos(modalPosRef.current)
    window.removeEventListener('mousemove', onMouseMoveDrag)
    window.removeEventListener('mouseup', onMouseUpDrag)
  }, [onMouseMoveDrag])

  const onTitleMouseDown = (e: React.MouseEvent) => {
    dragging.current = true
    document.body.style.userSelect = 'none'
    dragData.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: modalPosRef.current.x,
      origY: modalPosRef.current.y,
    }
    window.addEventListener('mousemove', onMouseMoveDrag)
    window.addEventListener('mouseup', onMouseUpDrag)
  }

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(translatedText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch { /* noop */ }
  }, [translatedText])

  const handleClose = useCallback(() => {
    abortRef.current?.abort()
    setPhase('hidden')
  }, [])

  if (phase === 'hidden') return null

  // Phase: small icon at selection end
  if (phase === 'icon') {
    return createPortal(
      <button
        ref={iconRef}
        onClick={handleIconClick}
        className="fixed z-[14030] p-1.5 rounded-full bg-popover text-popover-foreground shadow-lg border border-border hover:bg-accent transition-colors animate-in fade-in-0 zoom-in-50 duration-100"
        style={{ left: iconPos.x, top: iconPos.y }}
        title={t('selectionTranslation.title', 'Translation')}
      >
        <Languages className="h-4 w-4" />
      </button>,
      document.body,
    )
  }

  // Phase: modal (loading / result / error)
  return createPortal(
    <div
      ref={panelRef}
      className="app-window app-window--active fixed z-[14030] w-80 rounded-xl overflow-hidden shadow-2xl animate-in fade-in-0 zoom-in-95 duration-150"
      style={{ left: modalPos.x, top: modalPos.y }}
    >
      <div
        className="window-titlebar"
        onMouseDown={onTitleMouseDown}
      >
        <div className="window-title">
          <Languages className="w-4 h-4" />
          {t('selectionTranslation.title', 'Translation')}
        </div>
        <div className="window-controls">
          <button
            type="button"
            className="control close"
            aria-label={t('selectionTranslation.close', 'Close')}
            onClick={handleClose}
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>

      <div className="p-3">
        {phase === 'loading' && (
          <div className="flex items-center justify-center gap-2 py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-xs">{t('selectionTranslation.translating', 'Translating...')}</span>
          </div>
        )}

        {phase === 'result' && (
          <div>
            <div className="text-sm leading-relaxed max-h-60 overflow-y-auto pr-1 mb-3">
              {translatedText}
            </div>
            <div className="flex items-center gap-1 border-t border-border pt-2">
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                {copied ? <><Check className="h-3 w-3" />{t('selectionTranslation.copied', 'Copied!')}</>
                  : <><Copy className="h-3 w-3" />{t('selectionTranslation.copyTranslation', 'Copy')}</>}
              </button>
            </div>
          </div>
        )}

        {phase === 'error' && (
          <div className="py-2">
            <p className="text-xs text-destructive mb-3">{t('selectionTranslation.error', 'Translation failed')}</p>
            <button
              onClick={handleRetry}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <RotateCcw className="h-3 w-3" />{t('selectionTranslation.retry', 'Retry')}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}
